import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import UserAvatar from "../../common/UserAvatar";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import EditIcon from "@mui/icons-material/Edit";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { format, parseISO } from "date-fns";
import { useGetClassesQuery } from "../classroom/classApiSlice";
import { useUpdateStudentInClassroomMutation } from "../students/studentApiSlice";
import { useUploadFileMutation } from "../../../app/utilsApiSlice";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import {
  StyledSelect,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import CustomFileInput from "../../customComponents/CustomFileInput";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import toast from "../../../helpers/toast";

const validationSchema = yup.object({
  first_name: yup.string().required("Required"),
  last_name: yup.string().required("Required"),
  email: yup.string().email("Enter a valid email"),
  gender: yup.string().required("Required"),
  dob: yup.string(),
  address: yup.string(),
  phone: yup.string(),
});

function EditDialog({ open, onClose, student, classroomId }) {
  const [updateStudent, { isLoading }] = useUpdateStudentInClassroomMutation();
  const [uploadFile, { isLoading: fileLoading }] = useUploadFileMutation();

  const formik = useFormik({
    initialValues: {
      first_name: student?.first_name || "",
      last_name: student?.last_name || "",
      email: student?.email || "",
      dob: student?.dob ? format(parseISO(student.dob), "yyyy-MM-dd") : "",
      gender: student?.gender || "",
      address: student?.address || "",
      phone: student?.phone || "",
      photo: student?.photo || "",
      reg_number: student?.reg_number || "",
      parent_email: student?.parent_email || "",
      parent_email2: student?.parent_email2 || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        await updateStudent({
          classroom_id: classroomId,
          student_id: student.id,
          ...values,
        }).unwrap();
        toast.success("Student updated successfully");
        onClose();
      } catch {
        // error handled by RTK Query
      }
    },
  });

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadFile(formData).unwrap();
      if (response) {
        formik.setFieldValue(e.target.name, response.url);
      }
    } catch {}
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{ ".MuiPaper-root": { maxWidth: "700px", width: "100%" } }}
    >
      <Box sx={{ display: "flex", padding: 2, justifyContent: "flex-end" }}>
        <CloseSharpIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
      </Box>
      <Divider />
      <DialogContent>
        <StyledPaper>
          <Box className="paperHeader">Update Student Profile</Box>
          <Box className="paperBody">
            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container columnSpacing={1} rowSpacing={3}>
                <Grid item xs={12} lg={6}>
                  <FormControl fullWidth error={formik.touched.first_name && Boolean(formik.errors.first_name)}>
                    <Typography variant="formSubHeading">first name</Typography>
                    <StyledTextField
                      bold
                      id="first_name"
                      name="first_name"
                      value={formik.values.first_name}
                      onChange={formik.handleChange}
                      error={formik.touched.first_name && Boolean(formik.errors.first_name)}
                    />
                    <FormHelperText>{formik.touched.first_name && formik.errors.first_name}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <FormControl fullWidth error={formik.touched.last_name && Boolean(formik.errors.last_name)}>
                    <Typography variant="formSubHeading">last name</Typography>
                    <StyledTextField
                      bold
                      id="last_name"
                      name="last_name"
                      value={formik.values.last_name}
                      onChange={formik.handleChange}
                      error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                    />
                    <FormHelperText>{formik.touched.last_name && formik.errors.last_name}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">date of birth</Typography>
                    <StyledTextField
                      type="date"
                      id="dob"
                      name="dob"
                      value={formik.values.dob}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth error={formik.touched.gender && Boolean(formik.errors.gender)}>
                    <Typography variant="formSubHeading">gender</Typography>
                    <StyledSelect
                      displayEmpty
                      id="gender"
                      name="gender"
                      value={formik.values.gender}
                      onChange={formik.handleChange}
                      error={formik.touched.gender && Boolean(formik.errors.gender)}
                    >
                      <MenuItem value="" disabled />
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </StyledSelect>
                    <FormHelperText>{formik.touched.gender && formik.errors.gender}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">reg number</Typography>
                    <StyledTextField
                      id="reg_number"
                      name="reg_number"
                      value={formik.values.reg_number}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">address</Typography>
                    <StyledTextField
                      id="address"
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">parent email</Typography>
                    <StyledTextField
                      id="parent_email"
                      name="parent_email"
                      value={formik.values.parent_email}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">parent email 2</Typography>
                    <StyledTextField
                      id="parent_email2"
                      name="parent_email2"
                      value={formik.values.parent_email2}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">photo</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CustomFileInput
                        id="photo"
                        name="photo"
                        accept="image/*"
                        placeholder="Choose photo..."
                        value={formik.values.photo}
                        onChange={handleFileChange}
                      />
                      {fileLoading && <CircularProgress size={20} />}
                    </Stack>
                    {formik.values.photo && (
                      <UserAvatar src={formik.values.photo} name="" sx={{ mt: 1 }} />
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="flex-end">
                    <StyledLoadingButton
                      loading={isLoading}
                      variant="contained"
                      type="submit"
                    >
                      Save Changes
                    </StyledLoadingButton>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </StyledPaper>
      </DialogContent>
    </Dialog>
  );
}

function ManageClassStudents() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { classId } = useParams();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [page, setPage] = useState(0);
  const PER_PAGE = 10;

  const { data: students, isLoading } = useGetClassesQuery(
    `${classId}/students`,
    { skip: !classId }
  );

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setEditOpen(true);
  };

  const handleClose = () => {
    setEditOpen(false);
    setSelectedStudent(null);
  };

  return (
    <Box>
      <StyledPaper>
        <Box className="paperHeader">
          <Typography variant="paperHeading">Class Students</Typography>
        </Box>
        {isLoading && <LinearProgress />}
        {isMobile ? (
          <Box sx={{ p: 2 }}>
            {students?.slice(page * PER_PAGE, (page + 1) * PER_PAGE)?.map((student) => (
              <Box
                key={student.id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1.5,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <UserAvatar src={student.photo} name={`${student.first_name || ""} ${student.last_name || ""}`.trim()} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: "primary.main", fontWeight: 600, textTransform: "capitalize" }}>
                      {student.first_name} {student.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reg No: {student.reg_number || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Gender: {student.gender || "-"}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleEdit(student)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ))}
            {!isLoading && !students?.length && (
              <Typography variant="body2" color="text.secondary">
                No students found in this classroom.
              </Typography>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ pb: 4, overflowX: "auto" }}>
            <Table sx={{ width: "100%", boxShadow: "none" }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Photo</StyledTableCell>
                  <StyledTableCell>Reg No</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Gender</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students?.slice(page * PER_PAGE, (page + 1) * PER_PAGE)?.map((student) => (
                  <StyledTableRow key={student.id}>
                    <StyledTableCell>
                      <UserAvatar src={student.photo} name={`${student.first_name || ""} ${student.last_name || ""}`.trim()} />
                    </StyledTableCell>
                    <StyledTableCell>{student.reg_number}</StyledTableCell>
                    <StyledTableCell>
                      {student.first_name} {student.last_name}
                    </StyledTableCell>
                    <StyledTableCell>{student.gender}</StyledTableCell>
                    <StyledTableCell>
                      <IconButton size="small" onClick={() => handleEdit(student)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
                {!isLoading && !students?.length && (
                  <TableRow>
                    <StyledTableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No students found in this classroom.
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={students?.length ?? 0}
          page={page}
          rowsPerPage={PER_PAGE}
          rowsPerPageOptions={[PER_PAGE]}
          onPageChange={(_, newPage) => setPage(newPage)}
          sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
        />
      </StyledPaper>

      {selectedStudent && (
        <EditDialog
          open={editOpen}
          onClose={handleClose}
          student={selectedStudent}
          classroomId={classId}
        />
      )}
    </Box>
  );
}

export default ManageClassStudents;
